/**
 * Production Routes List
 * Liste des routes de production avec filtres
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { Plus, Play, Square, RefreshCw, Lock, Unlock, X, Save } from 'lucide-react';
import '../../../styles/admin/production/ProductionRoutes.css';

const ProductionRoutes = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date: '',
    status: '',
    service_type: ''
  });
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    route_name: '',
    service_type: 'fertilisation',
    route_date: new Date().toISOString().split('T')[0],
    crew_leader_id: '',
    planned_start_time: '08:00',
    planned_end_time: '17:00',
    notes: ''
  });

  useEffect(() => {
    fetchRoutes();
  }, [filters]);

  const fetchRoutes = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date) params.append('date', filters.date);
      if (filters.status) params.append('status', filters.status);
      if (filters.service_type) params.append('service_type', filters.service_type);

      const response = await api.get(`/api/production/routes?${params.toString()}`);
      setRoutes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching routes:', error);
      setLoading(false);
    }
  };

  const handleNewRoute = () => {
    setShowCreateModal(true);
  };

  const handleCreateRoute = async (e) => {
    e.preventDefault();
    
    try {
      setCreateLoading(true);
      const response = await api.post('/api/production/routes', formData);
      await fetchRoutes();
      setShowCreateModal(false);
      setFormData({
        route_name: '',
        service_type: 'fertilisation',
        route_date: new Date().toISOString().split('T')[0],
        crew_leader_id: '',
        planned_start_time: '08:00',
        planned_end_time: '17:00',
        notes: ''
      });
      
      // Navigate to the created route
      navigate(`/admin/production/routes/${response.data.id}`);
      setCreateLoading(false);
    } catch (error) {
      console.error('Erreur création route:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création');
      setCreateLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Brouillon', className: 'status-draft' },
      planned: { label: 'Planifiée', className: 'status-planned' },
      in_progress: { label: 'En cours', className: 'status-in-progress' },
      completed: { label: 'Terminée', className: 'status-completed' },
      cancelled: { label: 'Annulée', className: 'status-cancelled' }
    };
    const s = statusMap[status] || { label: status, className: '' };
    return <span className={`status-badge ${s.className}`}>{s.label}</span>;
  };

  if (loading) {
    return <div className="loading-container">Chargement...</div>;
  }

  return (
    <div className="production-routes-container">
      <div className="page-header">
        <div>
          <h1>Routes de Production</h1>
          <p className="subtitle">Gestion des routes terrain et calcul de rentabilité</p>
        </div>
        <button className="btn-primary" onClick={handleNewRoute}>
          <Plus size={20} />
          Nouvelle Route
        </button>
      </div>

      {/* Filtres */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Date</label>
          <input
            type="date"
            value={filters.date}
            onChange={(e) => setFilters({ ...filters, date: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>Statut</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">Tous</option>
            <option value="draft">Brouillon</option>
            <option value="planned">Planifiée</option>
            <option value="in_progress">En cours</option>
            <option value="completed">Terminée</option>
            <option value="cancelled">Annulée</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type de service</label>
          <select
            value={filters.service_type}
            onChange={(e) => setFilters({ ...filters, service_type: e.target.value })}
          >
            <option value="">Tous</option>
            <option value="fertilisation">Fertilisation</option>
            <option value="tonte">Tonte</option>
            <option value="deneigement">Déneigement</option>
            <option value="aerification">Aérification</option>
          </select>
        </div>

        <button className="btn-secondary" onClick={fetchRoutes}>
          <RefreshCw size={16} />
          Actualiser
        </button>
      </div>

      {/* Tableau */}
      <div className="routes-table-container">
        <table className="routes-table">
          <thead>
            <tr>
              <th>Numéro</th>
              <th>Date</th>
              <th>Service</th>
              <th>Chef d'équipe</th>
              <th>Stops</th>
              <th>Statut</th>
              <th>Revenus</th>
              <th>Coûts</th>
              <th>Profit</th>
              <th>Marge</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {routes.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">
                  Aucune route trouvée. Créez votre première route de production.
                </td>
              </tr>
            ) : (
              routes.map((route) => (
                <tr key={route.id} onClick={() => navigate(`/admin/production/routes/${route.id}`)}>
                  <td className="route-number">
                    {route.is_locked && <Lock size={14} className="lock-icon" />}
                    {route.route_number}
                  </td>
                  <td>{new Date(route.route_date).toLocaleDateString('fr-CA')}</td>
                  <td>
                    <span className="service-badge">{route.service_type}</span>
                  </td>
                  <td>{route.crew_leader_id || '-'}</td>
                  <td>
                    <span className="stops-count">
                      {route.completed_stops} / {route.total_stops}
                    </span>
                  </td>
                  <td>{getStatusBadge(route.status)}</td>
                  <td className="currency">${route.total_service_revenue.toFixed(2)}</td>
                  <td className="currency">${route.total_cost.toFixed(2)}</td>
                  <td className={`currency ${route.total_profit >= 0 ? 'positive' : 'negative'}`}>
                    ${route.total_profit.toFixed(2)}
                  </td>
                  <td className="margin">
                    <span className={route.margin_percent >= 0 ? 'positive' : 'negative'}>
                      {route.margin_percent.toFixed(1)}%
                    </span>
                  </td>
                  <td className="actions" onClick={(e) => e.stopPropagation()}>
                    <button className="btn-icon" title="Voir détails">
                      <Play size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Création Route */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Créer une Nouvelle Route</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreateRoute} className="modal-form">
              <div className="form-group">
                <label>Nom de la Route *</label>
                <input
                  type="text"
                  required
                  value={formData.route_name}
                  onChange={(e) => setFormData({...formData, route_name: e.target.value})}
                  placeholder="Ex: Route Nord - Fertilisation Mars"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type de Service *</label>
                  <select
                    required
                    value={formData.service_type}
                    onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                  >
                    <option value="fertilisation">Fertilisation</option>
                    <option value="tonte">Tonte</option>
                    <option value="deneigement">Déneigement</option>
                    <option value="aerification">Aérification</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.route_date}
                    onChange={(e) => setFormData({...formData, route_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Heure Début Prévue</label>
                  <input
                    type="time"
                    value={formData.planned_start_time}
                    onChange={(e) => setFormData({...formData, planned_start_time: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Heure Fin Prévue</label>
                  <input
                    type="time"
                    value={formData.planned_end_time}
                    onChange={(e) => setFormData({...formData, planned_end_time: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Chef d'Équipe</label>
                <input
                  type="text"
                  value={formData.crew_leader_id}
                  onChange={(e) => setFormData({...formData, crew_leader_id: e.target.value})}
                  placeholder="ID ou nom du chef d'équipe"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Notes additionnelles sur la route..."
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={createLoading}>
                  <Save size={16} />
                  {createLoading ? 'Création...' : 'Créer la Route'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionRoutes;
